import { PayPalButtons } from "@paypal/react-paypal-js";
import { useState } from "react";
import axios from "axios";

const   PayButton = ({ amount, onClick, userData, programInfo }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  // Base URL for API calls
  const baseUrl = process.env.REACT_APP_NODE_ENV === 'production' 
    ? process.env.REACT_APP_PRODUCTION_BASE_URL 
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL;

  const handleCreateOrder = async () => {
    try {
      setIsProcessing(true);
      
      // Call the onClick callback to register the user first
      if (onClick && typeof onClick === 'function') {
        console.log("[PayButton] Registering user first");
        const registrationData = await onClick();
        if (!registrationData || !registrationData.success) {
          console.error("[PayButton] User registration failed");
          setIsProcessing(false);
          throw new Error('User registration failed');
        }
        
        // Store user ID in session storage for later use
        if (registrationData.userId) {
          window.sessionStorage.setItem('registeredUserId', registrationData.userId);
        }
      }
      
      if (!amount || amount <= 0) {
        console.error("[PayButton] Invalid amount:", amount);
        setIsProcessing(false);
        throw new Error('Invalid payment amount');
      }

      const res = await fetch(`${baseUrl}/api/paypal/create`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ amount: Number(amount).toFixed(2) }),
      });
      
      if (!res.ok) {
        let errorMessage;
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || 'Failed to create PayPal order';
        } catch {
          errorMessage = await res.text();
        }
        console.error("[PayButton] Create order failed:", errorMessage);
        setIsProcessing(false);
        throw new Error(errorMessage);
      }

      const data = await res.json();
      
      if (!data.success || !data.paymentId || !data.approvalUrl) {
        console.error("[PayButton] Invalid server response:", data);
        setIsProcessing(false);
        throw new Error('Invalid response from server');
      }

      // Store paymentId for later use
      window.sessionStorage.setItem('paypalPaymentId', data.paymentId);
      
      setIsProcessing(false);
      return data.paymentId;
    } catch (err) {
      console.error("[PayButton] Error in createOrder:", err);
      console.error("[PayButton] Error stack:", err.stack);
      setIsProcessing(false);
      window.location.href = "/payment-error";
      throw err;
    }
  };

  const handleApprove = async (data, actions) => {
    try {
      setIsProcessing(true);
      
      // Get the paymentId and userId we stored earlier
      const paymentId = window.sessionStorage.getItem('paypalPaymentId');
      const userId = window.sessionStorage.getItem('registeredUserId');
      
      if (!paymentId) {
        console.error("[PayButton] No paymentId found in session storage");
        setIsProcessing(false);
        throw new Error('Payment ID not found');
      }

      if (!data.payerID) {
        console.error("[PayButton] No PayerID received from PayPal");
        setIsProcessing(false);
        throw new Error('PayerID not received');
      }

      const res = await fetch(`${baseUrl}/api/paypal/execute`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          paymentId: paymentId,
          payerId: data.payerID,
          userId: userId,
          amount: Number(amount).toFixed(2),
          programId: programInfo?.programId,
          programName: programInfo?.programName
        }),
      });
      
      if (!res.ok) {
        let errorMessage;
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || 'Failed to execute payment';
        } catch {
          errorMessage = await res.text();
        }
        console.error("[PayButton] Execute payment failed:", errorMessage);
        setIsProcessing(false);
        throw new Error(errorMessage);
      }

      const result = await res.json();
      
      if (result.success) {
        // Clear the stored data
        window.sessionStorage.removeItem('paypalPaymentId');
        
        // Redirect to success page with payment details
        const paymentDetails = encodeURIComponent(JSON.stringify(result));
        window.location.href = `/payment-success?details=${paymentDetails}`;
      } else {
        console.error("[PayButton] Payment execution failed:", result.error);
        setIsProcessing(false);
        throw new Error(result.error || "Payment execution failed");
      }
    } catch (err) {
      console.error("[PayButton] Error in onApprove:", err);
      console.error("[PayButton] Error stack:", err.stack);
      setIsProcessing(false);
      window.location.href = "/payment-error";
      throw err;
    }
  };

  const handleCancel = async () => {
    console.log("[PayButton] Payment cancelled by user");
    window.sessionStorage.removeItem('paypalPaymentId');
    setIsProcessing(false);
    
    try {
      // Get the userId we stored earlier
      const userId = window.sessionStorage.getItem('registeredUserId');
      
      if (userId) {
        // Update the payment status to canceled
        await axios.post(`${baseUrl}/api/user/update-payment-status`, {
          userId: userId,
          paymentStatus: 'canceled'
        });
        
        // Update session storage payment status
        const paymentDetails = window.sessionStorage.getItem('paymentDetails');
        if (paymentDetails) {
          const details = JSON.parse(paymentDetails);
          const updatedDetails = {
            ...details,
            paymentStatus: 'canceled'
          };
          window.sessionStorage.setItem('paymentDetails', JSON.stringify(updatedDetails));
        }
      }
    } catch (err) {
      console.error("[PayButton] Error updating payment status on cancel:", err);
    }
    
    // Redirect to cancel page
    window.location.href = "/payment-cancel";
  };

  return (
    <>
      {isProcessing && (
        <div style={{ marginBottom: "15px", textAlign: "center" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Processing payment...</span>
          </div>
          <p style={{ marginTop: "8px", fontSize: "14px" }}>Processing your payment...</p>
        </div>
      )}
      <PayPalButtons
        style={{
          layout: "horizontal",
          color: "blue",
          shape: "rect",
          label: "pay",
        }}
        createOrder={handleCreateOrder}
        onApprove={handleApprove}
        onError={(err) => {
          console.error("[PayButton] PayPal error:", err);
          console.error("[PayButton] Error details:", {
            message: err.message,
            stack: err.stack,
            data: err.data
          });
          setIsProcessing(false);
          window.location.href = "/payment-error";
        }}
        onCancel={handleCancel}
        disabled={isProcessing}
      />
    </>
  );
};

export default PayButton;
